import {
  createUnifiedTheme,
  createBaseThemeOptions,
  palettes,
  PageTheme,
} from '@backstage/theme';
import { alpha as muiAlpha, Theme } from '@mui/material/styles';

const fontFamily = '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';
const displayFontFamily =
  '"Space Grotesk", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif';
const monoFontFamily =
  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

// Mermaid appends a single `div.mermaidTooltip` to <body> the first time it
// renders a flowchart, and never sets `top` on it. Absolutely positioned with
// no offset, it lands at its static position — the very bottom of the app
// shell. The shell is exactly one viewport tall (`main` is the scroller, at
// `height: 100vh; overflow-y: auto`), so those few pixels push the document
// past the viewport and every TechDocs page with a diagram grows a second,
// almost empty scrollbar beside the real one.
//
// Pinning the idle tooltip to the top costs nothing: mermaid sets `top` and
// `left` inline the moment it actually shows the tooltip, and inline styles
// win over this rule.
const globalStyleOverrides = {
  MuiCssBaseline: {
    styleOverrides: {
      'body > .mermaidTooltip': { top: 0 },
    },
  },
};

// The palette follows the launch video: a deep night-sky navy, cyan as the
// main accent, lime for highlights and violet as a secondary glow. The DevOps
// banner adds the friendlier side (meadow green, sky blue, rounder shapes).
const night = '#06101D';
const nightRaised = '#0B1930';
const cyan = '#38D7FF';
const lime = '#DDFF6B';
const violet = '#9280FF';
const meadow = '#5BD48A';
const sky = '#3BA3E6';

// Light theme primary: a deep ocean take on the video's cyan. A royal blue
// (#2F4FD6) read as the browser's default link colour. 5.7:1 on white.
const ocean = '#0B6E94';
const oceanDeep = '#08516E';
const ds24BrightBlue = '#199ADC';

/**
 * Page headers: a navy field with two soft glows and a faint grid, the same
 * backdrop as the video's title cards. Repeating gradients are used for the
 * grid because Backstage's Header sets `background-size: cover`, which would
 * stretch a sized single-tile gradient.
 */
const header = (
  base: [string, string],
  glowA: string,
  glowB: string,
): PageTheme => ({
  colors: base,
  shape: 'none',
  fontColor: '#F7FBFF',
  backgroundImage: [
    `radial-gradient(520px 260px at 88% -10%, ${glowA}, transparent 70%)`,
    `radial-gradient(460px 260px at 4% 130%, ${glowB}, transparent 70%)`,
    'repeating-linear-gradient(0deg, rgba(170,210,240,0.07) 0 1px, transparent 1px 44px)',
    'repeating-linear-gradient(90deg, rgba(170,210,240,0.07) 0 1px, transparent 1px 44px)',
    `linear-gradient(120deg, ${base[0]}, ${base[1]})`,
  ].join(', '),
});

const alpha = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const pageThemes = (mode: 'light' | 'dark'): Record<string, PageTheme> => {
  // Light mode lifts the headers towards the banner's sky; dark mode keeps
  // them at night so they don't glare against the dark canvas.
  const base: [string, string] =
    mode === 'light' ? ['#071A2E', '#0E4A6E'] : [night, nightRaised];
  const g = mode === 'light' ? 0.55 : 0.32;
  const brand = header(base, alpha(cyan, g), alpha(violet, g));
  const docs = header(base, alpha(meadow, g), alpha(cyan, g));
  const build = header(base, alpha(violet, g), alpha(cyan, g * 0.8));
  const quiet = header(base, alpha(sky, g * 0.7), alpha(violet, g * 0.6));
  return {
    home: brand,
    website: brand,
    app: brand,
    card: brand,
    documentation: docs,
    apis: docs,
    tool: build,
    service: build,
    library: quiet,
    other: quiet,
  };
};

const typography = {
  htmlFontSize: 16,
  fontFamily,
  h1: {
    fontSize: 38,
    fontWeight: 700,
    marginBottom: 10,
    fontFamily: displayFontFamily,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: 30,
    fontWeight: 700,
    marginBottom: 8,
    fontFamily: displayFontFamily,
    letterSpacing: '-0.015em',
  },
  h3: {
    fontSize: 24,
    fontWeight: 600,
    marginBottom: 6,
    fontFamily: displayFontFamily,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: 20,
    fontWeight: 600,
    marginBottom: 6,
    fontFamily: displayFontFamily,
  },
  h5: {
    fontSize: 17,
    fontWeight: 600,
    marginBottom: 4,
    fontFamily: displayFontFamily,
  },
  h6: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 2,
    fontFamily: displayFontFamily,
  },
  // The video's small caps labels ("01 — WHAT IT IS")
  overline: {
    fontFamily: monoFontFamily,
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '0.14em',
    lineHeight: 1.6,
  },
};

const components = (mode: 'light' | 'dark') => {
  const cardBorder =
    mode === 'light' ? '1px solid #E3EAF3' : '1px solid rgba(170,210,240,0.10)';
  const cardShadow =
    mode === 'light'
      ? '0 1px 2px rgba(16,24,40,0.04), 0 6px 20px rgba(16,24,40,0.06)'
      : '0 1px 2px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.25)';
  return {
    ...globalStyleOverrides,
    MuiButton: {
      styleOverrides: {
        // Keep Backstage's default: text buttons keep the normal padding
        text: { padding: undefined },
        root: {
          borderRadius: 10,
          textTransform: 'none' as const,
          fontWeight: 600,
          letterSpacing: 0,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'unset' },
        rounded: { borderRadius: 16 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: cardBorder,
          boxShadow: cardShadow,
          display: 'flex',
          flexDirection: 'column' as const,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { paddingBottom: 0 },
      },
    },
    MuiChip: {
      styleOverrides: {
        // Backstage's default 8px trailing/bottom margin stacks tag chips in
        // table cells into tall towers; a small even gap keeps rows level
        root: {
          borderRadius: 999,
          margin: '2px 6px 2px 0',
        },
        label: { fontWeight: 500 },
      },
    },
    // Lists and tables. Backstage's defaults break words anywhere
    // ("Digistore2|4"), squeeze wrapped lines to `line-height: 1` and zebra
    // stripe every other row. Instead: words wrap at spaces, rows get room to
    // breathe and are separated by hairlines, the hovered row is tinted.
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          '&:nth-of-type(odd)': { backgroundColor: 'transparent' },
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: muiAlpha(theme.palette.primary.main, 0.04),
          },
        }),
        hover: { '&:hover': { cursor: 'pointer' } },
        head: {
          '&:nth-of-type(odd)': { backgroundColor: 'transparent' },
          '&:hover': { backgroundColor: 'transparent' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          wordBreak: 'normal' as const,
          overflowWrap: 'break-word' as const,
          overflow: 'hidden',
          verticalAlign: 'middle',
          lineHeight: 1.4,
          margin: 0,
          padding: theme.spacing(1.5, 1.5),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        sizeSmall: ({ theme }: { theme: Theme }) => ({
          padding: theme.spacing(1, 1.5),
        }),
        head: ({ theme }: { theme: Theme }) => ({
          color: theme.palette.textSubtle,
          fontWeight: 600,
          lineHeight: 1.4,
        }),
      },
    },
    // The catalog/API tables' column headers: quiet small caps on a tinted
    // band rather than bold capitals between two grey rules
    BackstageTableHeader: {
      styleOverrides: {
        header: ({ theme }: { theme: Theme }) => ({
          padding: theme.spacing(1.25, 1.5),
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.textSubtle,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase' as const,
          whiteSpace: 'nowrap' as const,
          position: 'static' as const,
        }),
      },
    },
    BackstageTableToolbar: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          padding: theme.spacing(2, 1, 2, 2),
        }),
        title: {
          '& > h6': {
            fontFamily: displayFontFamily,
            fontSize: 17,
            fontWeight: 600,
          },
        },
      },
    },
    // Catalog sidebar "Personal / Digistore24" picker: a hard-coded
    // rgba(0,0,0,.11) box upstream; make it a card like everything else
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: ({ theme }: { theme: Theme }) => ({
          backgroundColor: theme.palette.background.paper,
          border: cardBorder,
          boxShadow: 'none',
          borderRadius: 16,
          overflow: 'hidden',
          margin: theme.spacing(1, 0),
        }),
        title: ({ theme }: { theme: Theme }) => ({
          margin: theme.spacing(1.5, 0, 0.5, 2),
          fontFamily: monoFontFamily,
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.14em',
          color: theme.palette.textSubtle,
        }),
        groupWrapper: ({ theme }: { theme: Theme }) => ({
          margin: theme.spacing(0.5, 1, 1, 1),
        }),
        menuItem: {
          minHeight: 40,
          borderRadius: 10,
        },
      },
    },
    // Entity page tabs: sentence case instead of small shouty capitals
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          textTransform: 'none' as const,
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0,
          padding: '18px 20px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 10 },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          backgroundColor: nightRaised,
          fontSize: 12,
        },
      },
    },
  };
};

export const velocityLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: { main: ocean, dark: oceanDeep },
      secondary: { main: ds24BrightBlue },
      background: {
        default: '#F4F7FB',
        paper: '#FFFFFF',
      },
      banner: {
        ...palettes.light.banner,
        info: ocean,
      },
      navigation: {
        background: night,
        indicator: cyan,
        color: '#A9B8CF',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: nightRaised,
        },
        submenu: {
          background: nightRaised,
        },
      },
      text: {
        primary: '#0F1A2E',
        secondary: '#566A85',
      },
      border: '#E3EAF3',
      textSubtle: '#566A85',
      link: ocean,
      linkHover: oceanDeep,
    },
  }),
  fontFamily,
  typography,
  defaultPageTheme: 'home',
  pageTheme: pageThemes('light'),
  components: components('light'),
});

export const velocityDarkTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.dark,
      primary: { main: cyan, dark: '#1FB8E0', contrastText: night },
      secondary: { main: lime, contrastText: night },
      background: {
        default: '#081322',
        paper: '#0F1C30',
      },
      banner: {
        ...palettes.dark.banner,
        info: '#0E4A6E',
      },
      navigation: {
        background: '#040B15',
        indicator: cyan,
        color: '#8C9CB5',
        selectedColor: '#FFFFFF',
        navItem: {
          hoverBackground: '#0E1A2D',
        },
        submenu: {
          background: '#0A1424',
        },
      },
      text: {
        primary: '#E8EEF7',
        secondary: '#9AAAC2',
      },
      border: 'rgba(170,210,240,0.12)',
      textSubtle: '#9AAAC2',
      link: cyan,
      linkHover: '#7FE6FF',
    },
  }),
  fontFamily,
  typography,
  defaultPageTheme: 'home',
  pageTheme: pageThemes('dark'),
  components: components('dark'),
});

/** Shared with the home page, so the hero uses the same accents as the theme */
export const accents = { night, nightRaised, cyan, lime, violet, meadow };
export { monoFontFamily };
